import React from "react";
import { useLocation } from "react-router-dom";
import { ADSENSE_CLIENT, ensureAdsenseLoaded } from "@/lib/adsenseLoader";
import { isAdsenseAutoAdsPath } from "@/lib/adsensePolicy";

const CONSENT_KEY = "marstick_cookie_consent_v2";

function hasAdvertisingConsent() {
  try {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (!saved) return false;
    if (saved === "accepted") return true;
    if (saved === "rejected") return false;
    return JSON.parse(saved)?.advertising === true;
  } catch {
    return false;
  }
}

export default function AdSenseAutoAds() {
  const location = useLocation();
  const lastPushedPath = React.useRef("");
  const [consented, setConsented] = React.useState(() => typeof window !== "undefined" && hasAdvertisingConsent());
  const autoAdsEnabled = consented && isAdsenseAutoAdsPath(location.pathname, location.search);

  React.useEffect(() => {
    const onConsent = () => setConsented(hasAdvertisingConsent());
    window.addEventListener("marstick:consent-updated", onConsent);
    window.addEventListener("storage", onConsent);
    return () => {
      window.removeEventListener("marstick:consent-updated", onConsent);
      window.removeEventListener("storage", onConsent);
    };
  }, []);

  React.useEffect(() => {
    if (!autoAdsEnabled || typeof window === "undefined") return;

    const routeKey = `${location.pathname}${location.search}`;
    const runAutoAds = () => {
      if (lastPushedPath.current === routeKey) return;
      lastPushedPath.current = routeKey;

      ensureAdsenseLoaded().then((loaded) => {
        if (!loaded) return;
        try {
          window.adsbygoogle = window.adsbygoogle || [];
          window.adsbygoogle.push({
            google_ad_client: ADSENSE_CLIENT,
            enable_page_level_ads: true,
          });
        } catch {
          // AdSense can reject duplicate Auto ads pushes on some SPA transitions.
        }
      });
    };

    const schedule = window.requestIdleCallback
      ? window.requestIdleCallback(runAutoAds, { timeout: 1800 })
      : window.setTimeout(runAutoAds, 1200);

    return () => {
      if (window.cancelIdleCallback && typeof schedule === "number") window.cancelIdleCallback(schedule);
      else window.clearTimeout(schedule);
    };
  }, [autoAdsEnabled, location.pathname, location.search]);

  return null;
}